import { FsId } from '@fullstory/recording-api';

/**
 * An unambiguous, immutable path, starting at the root document, to a document
 * within a page. The path identifies a sequence of nested child documents which
 * much be traversed to reach the target document. Each child document is
 * identified by the fsid of its frame within the containing document and the
 * timestamp at which that frame was navigated to the child document.
 *
 * DocumentPaths are interned, so reference and value equality are equivalent
 * for them. In particular, DocumentPaths are safe to use as Map<> and Set<>
 * keys.
 *
 * Because DocumentPaths take time into consideration, they're stable and
 * unambiguous over time, but it's important to note that the document they
 * identify may not exist for the entire lifetime of the page.
 */
export class DocumentPath {
  // TODO(bendean): Consider using WeakRef/finalizers to garbage collect
  // this _interned cache (here and in the other classes defined in this
  // module).
  private static _interned = new Map<string, DocumentPath>();

  readonly #asFramePath!: FramePath;
  readonly asString!: string;

  constructor(public readonly components: readonly DocumentPathComponent[] = []) {
    const asString = `/${this.components.join('/')}`;
    const existing = DocumentPath._interned.get(asString);
    if (existing) {
      return existing;
    }

    this.asString = asString;
    this.#asFramePath = documentPathComponentsToFramePath(this.components);
    DocumentPath._interned.set(this.asString, this);
  }

  get [Symbol.toStringTag]() {
    return `DocumentPath ${this.asString}`;
  }

  toString(): string {
    return this.asString;
  }

  equals(other: Readonly<DocumentPath>): boolean {
    return this === other;
  }

  /**
   * @returns a new DocumentPath with one additional path component. The document
   * identified by the new DocumentPath is a child document of this
   * DocumentPath's document.
   */
  extend(nextFrameId: number, nextEventIndex: number): DocumentPath {
    return new DocumentPath(
      this.components.concat(new DocumentPathComponent(nextFrameId, nextEventIndex)),
    );
  }

  /**
   * @returns the FramePath to the frame which contains the document identified
   * by this DocumentPath.
   */
  toFramePath(): FramePath {
    return this.#asFramePath;
  }
}

/**
 * A component of a DocumentPath. Each component unambiguously identifies a
 * specific subdocument within its containing document. The subdocument is
 * identified by both the fsid of the frame it lives in and the timestamp at
 * which the frame was navigated to that document.
 *
 * Usually you don't need to create these directly, since it's more typical to
 * create a DocumentPath for the root document and then add components via
 * {@link DocumentPath#extend}.
 */
export class DocumentPathComponent {
  readonly asString: string;

  constructor(public readonly frameId: FsId, public readonly when: number) {
    this.asString = `${this.frameId}@${this.when}`;
  }

  get [Symbol.toStringTag]() {
    return `DocumentId ${this.asString}`;
  }

  toString(): string {
    return this.asString;
  }

  equals(other: DocumentPathComponent): boolean {
    return this.frameId === other.frameId && this.when === other.when;
  }
}

/**
 * An immutable, human-readble path, starting at the root frame, to a particular
 * <iframe> in the recorded page.
 *
 * FramePaths are interned, so reference and value equality are equivalent
 * for them. In particular, FramePaths are safe to use as Map<> and Set<>
 * keys.
 *
 * Because <iframe> elements may be navigated over the course of a recording,
 * different documents may live in the same frame at different times, and the
 * same FramePath may refer to different frames over time. FramePaths thus do
 * not unambiguously identify a document *or* a frame in isolation; they need to
 * be associated with a timeline or some other kind of time information to be
 * meaningful. Use a DocumentPath if you need an identifier that's stable over
 * time.
 */
export class FramePath {
  private static _interned = new Map<string, FramePath>();

  readonly components: FramePathComponents;
  readonly asString: string;

  /**
   * Create a FramePath from frame information in the format used by RawEvents.
   * @param rawFrameId - Equivalent to {@link RawEvent#FId}.
   * @param parentIds - Equivalent to {@link RawEvent#PIds}.
   */
  constructor(rawFrameId?: FsId, parentIds?: readonly FsId[]) {
    this.components = createFramePathComponents(rawFrameId, parentIds);
    this.asString = `/${this.components.join('/')}`;

    const existing = FramePath._interned.get(this.asString);
    if (existing) {
      return existing;
    }
    FramePath._interned.set(this.asString, this);
  }

  get [Symbol.toStringTag]() {
    return `FramePath ${this.asString}`;
  }

  toString(): string {
    return this.asString;
  }

  static fromString(framePathStr: string): FramePath | undefined {
    const existing = FramePath._interned.get(framePathStr);
    if (existing) {
      return existing;
    }

    // Split the path into components, ignoring the leading slash.
    const [_, ...components] = framePathStr.split('/');
    if (components.length === 0) {
      return new FramePath();
    }

    const frameId = Number(components.pop());
    if (Number.isNaN(frameId)) {
      console.error('Invalid frame id in frame path:', framePathStr);
      return undefined;
    }

    const parentIds = components.map(Number);
    if (parentIds.some(Number.isNaN)) {
      console.error('Invalid parent id in frame path:', framePathStr);
      return undefined;
    }

    return new FramePath(frameId, parentIds);
  }

  equals(other: Readonly<FramePath>): boolean {
    return this === other;
  }

  equalsOrIsDescendantOf(other: Readonly<FramePath>): boolean {
    if (this === other) {
      return true;
    }
    if (this.components.length < other.components.length) {
      return false;
    }
    for (let i = 0; i < other.components.length; i++) {
      if (this.components[i] !== other.components[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * @returns a new FramePath with one additional path component. The frame
   * identified by the new FramePath is a child frame of this
   * FramePath's frame.
   */
  extend(nextFrameId: number): FramePath {
    return new FramePath(nextFrameId, this.components);
  }
}

/**
 * A path from the root frame to a frame in a descendant document. Each element
 * in the path is the fsid of the node containing the next frame in the chain.
 * The root frame is identified by [].
 */
export type FramePathComponents = readonly FsId[];

/**
 * Create an array of FramePath components from frame information in the format
 * used by RawEvents.
 *
 * You generally don't need to call this directly; create a FramePath object
 * instead.
 *
 * @param rawFrameId - Equivalent to {@link RawEvent#FId}.
 * @param parentIds - Equivalent to {@link RawEvent#PIds}.
 */
export function createFramePathComponents(
  rawFrameId: FsId | undefined,
  parentIds?: readonly FsId[],
): FramePathComponents {
  const frameId = rawFrameId || 0;

  if (!parentIds || parentIds.length == 0) {
    // The root frame is frame id 0 for the web.  Native mobile embedded web
    // sessions recorded after #22747 also use frame id 0.  Earlier native
    // mobile web sessions used frame id -1.
    if (frameId <= 0) {
      return [];
    }

    // If we have a frame id but no parents, the path has a single level of
    // <iframe> nesting.
    return [frameId];
  }

  return parentIds.concat(frameId);
}

/**
 * @returns true if the provided RawEvent frame information corresponds to the
 * root frame.
 * @param rawFrameId - Equivalent to {@link RawEvent#FId}.
 * @param parentIds - Equivalent to {@link RawEvent#PIds}.
 */
export function isRootFrame(rawFrameId: FsId | undefined, parentIds?: FsId[]): boolean {
  if (parentIds && parentIds.length > 0) {
    return false;
  }
  if (rawFrameId === undefined) {
    return true;
  }
  return rawFrameId <= 0;
}

function documentPathComponentsToFramePath(
  components: readonly DocumentPathComponent[],
): FramePath {
  const frameIds = components.map(c => c.frameId);
  const last = frameIds.length - 1;
  const frame = frameIds[last];
  const parents = frameIds.slice(0, last);
  return new FramePath(frame, parents);
}

/**
 * An immutable identifier for the node unique across the whole recorded
 * page. In a single-document context, use FsID instead of NodePath.
 *
 * May refer to different nodes at different points in time.
 *
 * Interned like {@link FramePath} and {@link DocumentPath}.
 */
export class NodePath {
  private static _interned = new Map<string, NodePath>();
  readonly asString: string;

  constructor(public readonly framePath: FramePath, public readonly nodeId: FsId) {
    this.asString = `${framePath.toString()}::${nodeId}`;
    const existing = NodePath._interned.get(this.toString());
    if (existing) {
      return existing;
    }
    NodePath._interned.set(this.toString(), this);
  }

  get [Symbol.toStringTag]() {
    return `NodePath ${this.asString}`;
  }

  toString(): string {
    return this.asString;
  }

  isDescendantOf(framePath: Readonly<FramePath>): boolean {
    return this.framePath.equalsOrIsDescendantOf(framePath);
  }

  static fromString(nodePathStr: string): NodePath | undefined {
    const existing = NodePath._interned.get(nodePathStr);
    if (existing) {
      return existing;
    }

    const [framePathString, nodeIdString] = nodePathStr.split('::');
    if (!framePathString || !nodeIdString) {
      console.error('Invalid node path:', nodePathStr);
      return undefined;
    }

    const framePath = FramePath.fromString(framePathString);
    if (!framePath) {
      console.error('Invalid frame path in node path:', nodePathStr);
      return undefined;
    }

    const nodeId = Number(nodeIdString);
    if (Number.isNaN(nodeId)) {
      console.error('Invalid node id in node path:', nodePathStr);
      return undefined;
    }

    return new NodePath(framePath, nodeId);
  }
}

/**
 * An immutable identifier for a view, or a node within a webview, that's unique
 * for a given frame + page + session.
 *
 * May refer to different views or nodes at different points in time.
 *
 * TODO(https://fullstory.atlassian.net/browse/MOCA-9735): reference all required pieces of data for uniqueness (frame + page + session)
 *
 * Interned like {@link NodePath}.
 */
export class ViewPath {
  private static _interned = new Map<string, ViewPath>();
  readonly asString: string;

  constructor(public readonly viewId: string, public readonly webViewNodePath?: NodePath) {
    this.asString = `/~${viewId}${webViewNodePath ?? ''}`;
    const existing = ViewPath._interned.get(this.toString());
    if (existing) {
      return existing;
    }
    ViewPath._interned.set(this.toString(), this);
  }

  get [Symbol.toStringTag]() {
    return `ViewPath ${this.asString}`;
  }

  toString(): string {
    return this.asString;
  }

  /** @returns true if this ViewPath identifies a node within a WebView. */
  isWebViewPath(): boolean {
    return !!this.webViewNodePath;
  }

  /**
   * @returns true if this ViewPath identifies a node within the WebView with
   * the given viewId.
   */
  isWithinWebView(viewId: string): boolean {
    return this.isWebViewPath() && this.viewId === viewId;
  }

  static fromString(viewPathStr: string): ViewPath | undefined {
    const existing = ViewPath._interned.get(viewPathStr);
    if (existing) {
      return existing;
    }

    // Check that the path starts with '/~'. The ids for views are always
    // prefixed with '~' so we can distinguish them from frame ids, so any valid
    // view path will start with '/~'.
    if (!viewPathStr.startsWith('/~')) {
      console.error('Invalid view path:', viewPathStr);
      return undefined;
    }

    // If there's no node path, the view path is just '/~<viewId>'.
    const nodePathStart = viewPathStr.indexOf('/', 2);
    if (nodePathStart === -1) {
      const viewId = viewPathStr.slice(2);
      return new ViewPath(viewId);
    }

    // Otherwise, the view path is '/~<viewId>/<nodePath>'. We'll extract the
    // view id and hand off the rest to NodePath#fromString().
    const viewId = viewPathStr.slice(2, nodePathStart);
    if (viewId.length === 0) {
      console.error('Invalid view id in view path:', viewId);
      return undefined;
    }

    const nodePathStr = viewPathStr.slice(nodePathStart);
    const nodePath = NodePath.fromString(nodePathStr);
    if (!nodePath) {
      console.error('Invalid node path in view path:', nodePathStr);
      return undefined;
    }

    return new ViewPath(viewId, nodePath);
  }
}
